// Imports
import { faArrowUp, faCircleQuestion, faSearch, faThumbsUp, faWrench } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components/macro';
import company from '../../../shareables/company';
import AnchorButton from '../../../shareables/foundation/front-end/components/buttons/anchor';
import Button from '../../../shareables/foundation/front-end/components/buttons/standard';
import { useAppSelector } from '../../../shareables/foundation/front-end/redux/hooks';
import theme from '../../../shareables/theme';
import modalHelpers from '../../../shareables/front-end/modal/modal-helpers';
import planMapping from '../../../shareables/utils/billing/plan-mapping';
import AdminControlsModal from './admin-controls-modal';


// Styled components
const Container = styled.div`
	background: #333;
`;

const Content = styled.div`
	max-width: 62.5rem;
	margin: 0 auto;
	padding: 0.4375rem 0 0.1875rem;
	font-size: 0.8125rem;
	line-height: 1;
	text-align: center;
	
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
`;

const Logo = styled.a`
	line-height: 1.25rem;
	
	position: absolute;
	left: 0;
	top: 0.5938rem;
	
	img {
		height: 1.0625rem;
	}
	
	@media only screen and (max-width: ${theme.breakpoints.mobile}px) {
		display: none;
	}
`;

const buttonStyles = `
    margin-bottom: .25rem;    
    padding: .0625rem .3125rem;
    font-size: .75rem;
    line-height: 1.5;
    border-radius: .1875rem;
	border: .0625rem solid #ccc;
	
	height: unset;
	
	&:not(:last-child) {
        margin-right: .25rem;
    }
    
    svg.svg-inline--fa {
			margin-right: 0.1875rem;
	}
`;

const StyledAnchorButton = styled(AnchorButton)`
	${buttonStyles}
	
	&[data-extra-control] {
		color: #fff !important;
		background-color: #5cb85c;
		border-color: #4cae4c;
		margin-left: 0.1875rem;
		
		&:hover,
		&:focus {
			background-color: #449d44 !important;
			border-color: #398439 !important;
		}
	}
`;

const StyledButton = styled(Button)`
	${buttonStyles}
`;


// Define the accepted props
interface Props {
	storybookLogo?: string;
}


// Function component
const AdminBar: React.FC<Props> = ({ storybookLogo }) => {
	// Use Redux functionality
	const website = useAppSelector((state) => state.self.website);
	
	
	// Return JSX
	return (
		<Container>
			<Content>
				<Logo href={`https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}`} target='_blank'>
					<img
						src={
							storybookLogo ?? `https://cdn.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/images/logos/admin-bar.png`
						}
						alt='Company logo'
					/>
				</Logo>
				
				<div>
					<StyledAnchorButton
						href={`https://admin.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/?websiteID=${website?.id ?? ''}`}
						color='plain'
					>
						<FontAwesomeIcon icon={faWrench} />
						Admin Portal
					</StyledAnchorButton>
					
					<StyledAnchorButton href={`https://help.${company.productionDomain ?? ''}`} target='_blank' color='plain'>
						<FontAwesomeIcon icon={faCircleQuestion} />
						Support
					</StyledAnchorButton>
					
					<StyledButton type='button' color='plain' onClick={() => modalHelpers.show('admin-controls-modal')}>
						<FontAwesomeIcon icon={faSearch} />
						View website without admin controls
					</StyledButton>
					
					{website && website.plan !== planMapping.deluxe && website.plan < Math.max(...Object.values(planMapping)) && (
						<StyledAnchorButton
							href={`https://admin.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/settings/billing/?websiteID=${website?.id ?? ''}`}
							data-extra-control
						>
							<FontAwesomeIcon icon={faArrowUp} />
							Upgrade plan!
						</StyledAnchorButton>
					)}
					
					{website?.subscriptionStatus === 'TRIAL' && (
						<StyledAnchorButton
							href={`https://admin.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/settings/billing/?websiteID=${website?.id ?? ''}`}
							data-extra-control
						>
							<FontAwesomeIcon icon={faThumbsUp} />
							Subscribe now!
						</StyledAnchorButton>
					)}
				</div>
			</Content>
			
			<AdminControlsModal />
		</Container>
	);
};

export default AdminBar;
